import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
    GridRenderEditCellParams,
    GridSingleSelectColDef,
    useGridApiContext,
} from "@mui/x-data-grid-premium";
import { AutocompleteOption, AutocompleteWrapper } from "../muiAutocomplete";
import { TransportOrderListItemDto } from "../../../models/transport/transportOrder";
import { useTranslation } from "react-i18next";
import { CustomerQuickCreateDialog } from "../../transport/components/customerQuickCreateDialog";

const CREATE_VALUE = "__create__";
const CACHE_EVENT = "orders-created-customers-cache-updated";

// Session cache for newly created customers (allows selecting inactive customers immediately)
const createdCustomersCache: Map<string, AutocompleteOption> = new Map();

const notifyCreatedCustomersCacheUpdated = () => {
    window.dispatchEvent(new CustomEvent(CACHE_EVENT));
};

const rememberCreatedCustomer = (opt: AutocompleteOption) => {
    if (!opt?.id) return;
    createdCustomersCache.set(opt.id, opt);
    if (createdCustomersCache.size > 20) {
        const firstKey = createdCustomersCache.keys().next().value as string | undefined;
        if (firstKey) createdCustomersCache.delete(firstKey);
    }
    notifyCreatedCustomersCacheUpdated();
};

const getRememberedCustomers = (): AutocompleteOption[] => Array.from(createdCustomersCache.values());

export const DataGridOrdersCustomerAutoComplete = ({
    id,
    field,
    value,
    hasFocus,
    colDef,
    row,
    refreshCustomers
}: GridRenderEditCellParams & { refreshCustomers?: () => void }) => {
    const apiRef = useGridApiContext();
    const { valueOptions } = colDef as GridSingleSelectColDef;
    const { t } = useTranslation();

    const [showDialog, setShowDialog] = useState(false);
    const [createdOptions, setCreatedOptions] = useState<AutocompleteOption[]>([]);
    const [cacheVersion, setCacheVersion] = useState(0);

    // Recompute options whenever the global cache notifies a change
    useEffect(() => {
        const handler = () => setCacheVersion((v) => v + 1);
        window.addEventListener(CACHE_EVENT, handler);
        return () => window.removeEventListener(CACHE_EVENT, handler);
    }, []);

    const handleValueChange = useCallback(
        async(newValue: string | null, r: TransportOrderListItemDto) => {
            // Apply the selected value to the current field
            await apiRef.current.setEditCellValue({ id, field, value: newValue });

            // If billing is empty, mirror the selected customer to billing per spec
            if (newValue && (r.billingCustomerId === null || r.billingCustomerId === undefined)) {
                await apiRef.current.setEditCellValue({ id, field: "billingCustomerId", value: newValue });
            }
        },
        [apiRef, field, id]
    );

    // Options coming from the backend for this column
    const baseOptions: AutocompleteOption[] = useMemo(
        () =>
            ((valueOptions as { value: string; label: string }[]) ?? []).map((o) => ({
                id: o.value,
                title: o.label,
            })),
        [valueOptions]
    );

    const createOption: AutocompleteOption = useMemo(
        () => ({
            id: CREATE_VALUE,
            title: t("transport.order.customerCreate.createNew"),
            extra: "create"
        }),
        [t]
    );

    // Merge options from cache, locally created in this instance, and backend; ensure current value is present
    const options: AutocompleteOption[] = useMemo(() => {
        const map = new Map<string, AutocompleteOption>();
        [...getRememberedCustomers(), ...createdOptions, ...baseOptions].forEach((o) => {
            if (o?.id) map.set(o.id, o);
        });

        // If the current cell has a value not returned by backend (e.g., inactive), inject it from cache
        const currentId = (typeof value === "string" ? value : null) ?? null;
        if (currentId && !map.has(currentId)) {
            const fromCache = createdCustomersCache.get(currentId);
            if (fromCache) {
                map.set(currentId, fromCache);
            } else {
                // Fall back to provide a label for inactive customers
                const nameField =
                    field === "senderCustomerId"
                        ? row.senderName
                        : field === "receiverCustomerId"
                            ? row.receiverName
                            : field === "billingCustomerId"
                                ? row.billingCustomerName
                                : undefined;
                if (nameField) {
                    map.set(currentId, { id: currentId, title: nameField });
                }
            }
        }

        return [createOption, ...map.values()];
    }, [baseOptions, createdOptions, createOption, cacheVersion, value, row, field]);

    const handleSelect = useCallback(
        (opt: AutocompleteOption | null) => {
            // Open quick-create dialog when "Create new" is chosen
            if (opt?.id === CREATE_VALUE) {
                setShowDialog(true);
                return;
            }
            void handleValueChange(opt?.id ?? null, row);
        },
        [handleValueChange, row]
    );

    const handleCreated = useCallback(
        async(cust: { id: string; name: string; active: boolean }) => {
            const opt: AutocompleteOption = { id: cust.id, title: cust.name };
            rememberCreatedCustomer(opt);
            setCreatedOptions((prev) => (prev.some((p) => p.id === opt.id) ? prev : [opt, ...prev]));
            await handleValueChange(cust.id, row);

            if (refreshCustomers) {
                refreshCustomers();
            }
        },
        [handleValueChange, row, refreshCustomers]
    );

    return (
        <>
            <AutocompleteWrapper
                options={options}
                size="small"
                defaultOpen={hasFocus}
                value={value}
                placeholder={t("common.typeToSeeOptions")}
                onChange={(val: AutocompleteOption) => handleSelect(val ?? null)}
                fullWidth
                textFieldProps={{
                    size: "small",
                    variant: "standard",
                    sx: { fontSize: "0.875rem" },
                }}
            />
            <CustomerQuickCreateDialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                onSaved={(cust) => {
                    void handleCreated(cust);
                    setShowDialog(false);
                }}
            />
        </>
    );
};